import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Panel } from '~/eds/src/components/Panel/Panel';
import { Info } from '~/eds/src/modules/Info/Info';
import { StatusType } from '~/eds/src/types/StatusType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';

import { Attempt } from '../types';
import { getResponseStatus, getStatusChip, getStringifyValue } from '../utils';

export const AttemptDetails = () => {
  const intl = useIntl();
  const enableWebhookExtraFeatures = useFlag(FlagType.EnableWebhooksComponents);
  const dispatch = useDispatch();

  const { activeAttempt, activeAttemptType: action } = useSelector(
    slices.webhooks.selectors.selectActiveAttempt,
  );

  if (!activeAttempt || action !== 'view') return null;

  const { status, text } = getStatusChip(
    activeAttempt.status ?? undefined,
    intl,
  );

  const getFooterActions = (attempt: Attempt) => {
    const actions = [];

    if (attempt.status === 'failed') {
      actions.push({
        text: intl.formatMessage({
          id: 'AttemptDetails.resendAction',
          defaultMessage: 'Resend',
          description: 'Resend button text',
        }),
        onClick: () => {
          dispatch(slices.webhooks.actions.setActiveAttemptType('resend'));
        },
      });
    }

    if (enableWebhookExtraFeatures) {
      actions.push({
        text: intl.formatMessage({
          id: 'AttemptDetails.archiveAction',
          defaultMessage: 'Archive',
          description: 'Archive button text',
        }),
        level: 'tertiary' as const,
        onClick: () => {
          dispatch(slices.webhooks.actions.setActiveAttemptType('archive'));
        },
      });
    }
    return actions;
  };

  return (
    <Panel
      actions={[
        {
          icon: 'x',
          text: intl.formatMessage({
            id: 'AttemptDetails.closeAction',
            defaultMessage: 'Close',
            description: 'Close button text',
          }),
          onClick: () => {
            dispatch(slices.webhooks.actions.resetAttemptConfiguration());
          },
        },
      ]}
      children={
        <Info
          sections={[
            {
              title: activeAttempt.endpointUrl,
              infos: [
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.eventTypeTitle',
                    defaultMessage: 'Event Type',
                    description: 'Event type field title',
                  }),
                  type: 'text' as const,
                  props: { text: activeAttempt.eventType },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.statusTitle',
                    defaultMessage: 'Status',
                    description: 'Status field title',
                  }),
                  type: 'chips' as const,
                  props: {
                    chips: [
                      {
                        status: status as StatusType,
                        text: text,
                      },
                    ],
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.messageIdTitle',
                    defaultMessage: 'Message ID',
                    description: 'Message ID field title',
                  }),
                  type: 'text' as const,
                  props: {
                    text: activeAttempt.messageId,
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.dateSentTitle',
                    defaultMessage: 'Date Sent',
                    description: 'Date sent field title',
                  }),
                  type: 'date' as const,
                  props: {
                    date: activeAttempt.dateSent
                      ? activeAttempt.dateSent
                      : undefined,
                    format: 'short_datetime',
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.responseStatusTitle',
                    defaultMessage: 'Response Status',
                    description: 'Response status field title',
                  }),
                  type: 'text' as const,
                  props: {
                    text: getResponseStatus(activeAttempt),
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.requestHeadersTitle',
                    defaultMessage: 'Request Headers',
                    description: 'Request headers field title',
                  }),
                  type: 'textarea' as const,
                  props: {
                    name: 'requestHeaders',
                    value: getStringifyValue(activeAttempt.requestHeaders),
                    readOnly: true,
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.requestBodyTitle',
                    defaultMessage: 'Request Body',
                    description: 'Request body field title',
                  }),
                  type: 'textarea' as const,
                  props: {
                    name: 'requestBody',
                    value: getStringifyValue(activeAttempt.requestBody),
                    readOnly: true,
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.responseHeadersTitle',
                    defaultMessage: 'Response Headers',
                    description: 'Response headers field title',
                  }),
                  type: 'textarea' as const,
                  props: {
                    name: 'requestHeaders',
                    value: getStringifyValue(activeAttempt.responseHeaders),
                    readOnly: true,
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'AttemptDetails.responseBodyTitle',
                    defaultMessage: 'Response Body',
                    description: 'Response body field title',
                  }),
                  type: 'textarea' as const,
                  props: {
                    name: 'responseBody',
                    value: getStringifyValue(activeAttempt.responseBody),
                    readOnly: true,
                  },
                },
              ],
            },
          ]}
        />
      }
      footer={{
        actions: getFooterActions(activeAttempt),
      }}
      hidden={{
        isHidden: false,
        onHide: () => {
          dispatch(slices.webhooks.actions.resetAttemptConfiguration());
        },
      }}
      position="right"
      title={intl.formatMessage({
        id: 'AttemptDetails.panelTitle',
        defaultMessage: 'Attempt Configuration',
        description: 'Panel title',
      })}
      width="m"
    />
  );
};
